import React, { useState } from 'react';
import { X, Trash2, TriangleAlert } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { InsuranceCarrier } from '@shared';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { ContactSupportButton } from '@/components/ContactSupportButton';
import { fundingPolicyTypeDefs } from '@shared';
import { Input } from '../ui/input';

interface CarrierAssignmentProps {
  bga: BgaEntry;
  carriers: InsuranceCarrierWithPolicyTypes[];
  onAssignCarrier: (bgaOrganizationId: string, carrierId: InsuranceCarrier) => void;
  onRemoveCarrier: (bgaOrganizationId: string) => void;
  onUpdateCarrierMeta: (bgaOrganizationId: string, carrierId: number, meta: BgaCarrierMeta) => void;
  isCarrierAssigned: (carrierId: InsuranceCarrier) => boolean;
}

export const CarrierAssignment: React.FC<CarrierAssignmentProps> = ({
  bga,
  carriers,
  onAssignCarrier,
  onRemoveCarrier,
  onUpdateCarrierMeta,
  isCarrierAssigned,
}) => {
  return (
    <div>
      <Label className="mb-3 block text-sm font-medium">Carrier</Label>
      {bga.assignedCarrierId ? (
        (() => {
          const carrier = carriers.find(c => c.insuranceCarrierId === bga.assignedCarrierId);
          const meta = bga.carrierMeta || {};
          if (!carrier) return null;

          return (
            <div className="mb-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {/*  */}
                  <span className="text-sm font-medium">{carrier.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveCarrier(bga.advisorOrganizationId)}
                  className="text-gray-500 hover:text-red-600"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
              <div className="ml-6 space-y-3 rounded-md border-l-2 border-gray-200 bg-gray-50 p-3 pl-4">
                <div className="space-y-2">
                  <Label
                    htmlFor={`writing-${bga.advisorOrganizationId}-${bga.assignedCarrierId}`}
                    className="text-xs"
                  >
                    Writing Code
                  </Label>
                  <Input
                    id={`writing-${bga.advisorOrganizationId}-${bga.assignedCarrierId}`}
                    value={meta?.writingCode || ''}
                    onChange={e =>
                      onUpdateCarrierMeta(bga.advisorOrganizationId, bga.assignedCarrierId!, {
                        writingCode: e.target.value,
                      })
                    }
                    placeholder="Enter writing code"
                    className="text-sm"
                  />
                </div>
              </div>
            </div>
          );
        })()
      ) : (
        <div className="space-y-2">
          <Label className="text-xs text-gray-700">Assign Carrier</Label>
          <Select
            onValueChange={carrierId =>
              onAssignCarrier(bga.advisorOrganizationId, parseInt(carrierId))
            }
          >
            <SelectTrigger className="max-w-md text-sm">
              <SelectValue placeholder="Select a carrier" />
            </SelectTrigger>
            <SelectContent>
              {carriers
                .filter(
                  carrier =>
                    !isCarrierAssigned(carrier.insuranceCarrierId) ||
                    carrier.insuranceCarrierId === bga.assignedCarrierId,
                )
                .map(carrier => (
                  <SelectItem
                    key={carrier.insuranceCarrierId}
                    value={carrier.insuranceCarrierId ? carrier.insuranceCarrierId.toString() : ''}
                  >
                    <div className="flex items-center gap-2">{carrier.name}</div>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
};

// Carrier Display Component
interface CarrierDisplayProps {
  bga: BgaEntry;
  carriers: InsuranceCarrierWithPolicyTypes[];
}

export const CarrierDisplay: React.FC<CarrierDisplayProps> = ({ bga, carriers }) => {
  if (!bga.assignedCarrierId) {
    return <p className="text-sm text-gray-700">No carrier assigned</p>;
  }

  const carrier = carriers.find(c => c.insuranceCarrierId === bga.assignedCarrierId);
  if (!carrier) return null;

  const meta = bga.carrierMeta;

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-700">Associated carrier:</p>
      <div className="flex items-center gap-2">
        <img
          src={`/logos/${carrier.name.toLowerCase()}.svg`}
          alt={carrier.name}
          className="size-6"
        />
        <span className="text-sm">{carrier.name}</span>
        {meta.writingCode && (
          <span className="text-xs text-gray-700">(Code: {meta.writingCode})</span>
        )}
      </div>
    </div>
  );
};

// Carrier Settings Loading State Component
export const CarrierSettingsLoadingState: React.FC = () => (
  <Card>
    <CardContent className="p-0">
      <div className="divide-y">
        {[1, 2].map(index => (
          <div
            key={index}
            className="animate-pulse p-6"
          >
            {/* Header Section Skeleton */}
            <div className="mb-6 flex items-start justify-between">
              <div className="flex items-center gap-4">
                {/* Logo skeleton */}
                <div className="h-12 w-12 rounded-lg bg-gray-200"></div>
                <div className="space-y-2">
                  {/* Carrier name skeleton */}
                  <div className="h-5 w-32 rounded bg-gray-200"></div>
                  {/* BGA badge skeleton */}
                  <div className="h-4 w-20 rounded bg-gray-200"></div>
                </div>
              </div>
            </div>

            {/* Products Section Skeleton */}
            <div className="space-y-4">
              {/* Available Products header */}
              <div className="flex items-center justify-between">
                <div className="h-4 w-28 rounded bg-gray-200"></div>
                <div className="h-3 w-16 rounded bg-gray-200"></div>
              </div>

              {/* Selected Products Grid Skeleton */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2].map(productIndex => (
                  <div
                    key={productIndex}
                    className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2"
                  >
                    <div className="h-4 w-20 rounded bg-gray-200"></div>
                    <div className="h-6 w-6 rounded bg-gray-200"></div>
                  </div>
                ))}
              </div>

              {/* Add Products Section Skeleton */}
              <div className="space-y-2">
                <div className="h-3 w-20 rounded bg-gray-200"></div>
                <div className="flex gap-2">
                  <div className="h-9 flex-1 rounded bg-gray-200"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

export const EmptyCarrierSettingsState: React.FC = () => (
  <Card>
    <CardContent className="flex flex-col items-center justify-center py-12">
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50">
        <TriangleAlert className="h-6 w-6 text-amber-600" />
      </div>

      <h3 className="mb-2 text-lg font-medium">No carriers available</h3>
      <p className="mb-6 text-center text-sm text-gray-700">
        Please contact support if you believe this is an error.
      </p>

      <ContactSupportButton
        subject="No carriers available - Need assistance"
        variant="outline"
      />
    </CardContent>
  </Card>
);

// Carrier Settings Card Component
interface CarrierSettingsCardProps {
  carrier: InsuranceCarrierWithPolicyTypes;
  bga?: BgaEntry;
  onToggleProduct: (carrierId: InsuranceCarrier, product: FundingPolicyType) => void;
  onRemoveProduct: (carrierId: InsuranceCarrier, product: FundingPolicyType) => void;
}

export const CarrierSettingsCard: React.FC<CarrierSettingsCardProps> = ({
  carrier,
  bga,
  onToggleProduct,
  onRemoveProduct,
}) => {
  const [selectedValue, setSelectedValue] = useState("");
  const carrierPolicyTypes = carrier.policyTypes;
  const noProductsAvailable = carrierPolicyTypes.length === 0;
  const allProductsSelected =
    !noProductsAvailable && carrier.selectedPolicyTypes.length === carrierPolicyTypes.length;

  const handleProductSelect = (product: string) => {
    onToggleProduct(carrier.insuranceCarrierId, parseInt(product));
    setSelectedValue(''); 
  };

  return (
    <div className="p-6">
      {/* Header Section */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 p-2 shadow-sm ring-1 ring-gray-200">
            <img
              src={`/logos/${carrier.name.toLowerCase()}.svg`}
              alt={carrier.name}
              className="h-8 w-8"
            />
          </div>
          <div className="space-y-1">
            <h4 className="text-lg font-semibold text-gray-900">{carrier.name}</h4>
            <div className="flex items-center gap-2">
              <Badge
                variant="secondary"
                className={`text-xs font-medium ${bga ? 'text-gray-700' : 'text-red-700'}`}
              >
                {bga ? `via ${bga.name}` : 'Unlinked Carrier'}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Products Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-medium text-gray-700">Available Products</Label>
          {carrier.selectedPolicyTypes.length > 0 && (
            <span className="text-xs text-gray-700">
              {carrier.selectedPolicyTypes.length} of {carrierPolicyTypes.length} selected
            </span>
          )}
        </div>

        {/* Selected Products Grid */}
        {carrier.selectedPolicyTypes.length > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {carrier.selectedPolicyTypes.map((product: FundingPolicyType) => (
              <div
                key={product}
                className="border-primary-200 bg-primary-50 flex items-center justify-between rounded-lg border px-3 py-2"
              >
                <span className="text-primary-dark text-sm font-medium">
                  {fundingPolicyTypeDefs[product].label}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveProduct(carrier.insuranceCarrierId, product)}
                  className="text-primary-dark hover:bg-primary-100 hover:text-primary-dark h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-lg border-2 border-dashed border-gray-200 p-6 text-center">
            <p className="mb-3 text-sm text-gray-700">No products selected for {carrier.name}</p>
            <p className="text-xs text-gray-500">Add products to start offering quotes</p>
          </div>
        )}

        {/* Add Products Section */}
        <div className="space-y-2">
          <Label className="text-xs font-medium text-gray-700">Add Product</Label>
          {carrierPolicyTypes.filter(product => !carrier.selectedPolicyTypes.includes(product))
            .length > 0 ? (
            <div className="flex gap-2">
              <Select
                value={selectedValue}
                onValueChange={handleProductSelect}
              >
                <SelectTrigger className="flex-1 text-sm text-gray-700">
                  <SelectValue placeholder="Select a product" />
                </SelectTrigger>
                <SelectContent>
                  {carrierPolicyTypes
                    .filter(product => !carrier.selectedPolicyTypes.includes(product))
                    .map(product => (
                      <SelectItem
                        key={product}
                        value={product?.toString()}
                      >
                        <div className="flex items-center gap-2">
                          <div className="bg-primary h-2 w-2 rounded-full"></div>
                          {fundingPolicyTypeDefs[product].label}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {/* Status Messages */}
          {allProductsSelected && (
            <div className="flex items-center gap-2 rounded-md bg-gray-50 px-3 py-2">
              <div className="h-1.5 w-1.5 rounded-full bg-gray-500"></div>
              <p className="text-xs font-medium text-gray-700">All products selected</p>
            </div>
          )}

          {noProductsAvailable && (
            <div className="flex items-center gap-2 rounded-md bg-yellow-50 px-3 py-2">
              <div className="h-1.5 w-1.5 rounded-full bg-yellow-500"></div>
              <p className="text-xs font-medium text-yellow-700">No products available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
